import React, { useEffect, useRef, useState } from "react";
import "./Filter.scss";
import { useTranslation } from "react-i18next";

const Filter = ({ colors, groups, filterParams, getFlowers }) => {
  const { t, i18n } = useTranslation();
  const filterRef = useRef(null);

  const [selectedGroupId, setSelectedGroupId] = useState(
    filterParams.groupId || ""
  );

  const [colorOpen, setColorOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const getCurrentLangKey = () => {
    const lang = String(i18n.resolvedLanguage || i18n.language || "lt")
      .split("-")[0]
      .toLowerCase();

    if (lang === "fi" || lang === "fin") {
      return "FIN";
    }

    return lang.toUpperCase();
  };

  const getGroupName = (group) => {
    if (!group) return "";

    const langKey = getCurrentLangKey();

    return (
      group.names?.[langKey] ||
      group.names?.LT ||
      group.name ||
      ""
    );
  };

  const sortOptions = [
    {
      id: "",
      name: t("filter.sort.nameAsc"),
    },
    {
      id: "price,ASC",
      name: t("filter.sort.priceAsc"),
    },
    {
      id: "price,DESC",
      name: t("filter.sort.priceDesc"),
    },
];

  useEffect(() => {
    setSelectedGroupId(filterParams.groupId || "");
  }, [filterParams.groupId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setColorOpen(false);
        setGroupOpen(false);
        setSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedColor =
    filterParams.color !== null &&
    filterParams.color !== undefined &&
    filterParams.color !== ""
      ? colors.find((color) => String(color) === String(filterParams.color))
      : null;

  const selectedGroup =
    selectedGroupId !== ""
      ? groups.find((group) => String(group.id) === String(selectedGroupId))
      : null;

  const selectedSort = sortOptions.find(
    (sort) => sort.id === (filterParams.sort ?? "")
  );

  const cleanParams = (params) => {
    const cleaned = { ...params };

    Object.keys(cleaned).forEach((key) => {
      if (
        cleaned[key] === "" ||
        cleaned[key] === null ||
        cleaned[key] === undefined
      ) {
        delete cleaned[key];
      }
    });

    return cleaned;
  };

  const applyFilter = (nextParams) => {
    const params = cleanParams({
      ...filterParams,
      ...nextParams,
      page: 0,
      pageNumber: 0,
    });

    getFlowers(params, null, true);
  };

  const handleSearch = (e) => {
    applyFilter({
      search: e.target.value,
    });
  };

  const handleColor = (colorId) => {
    applyFilter({
      color: colorId || "",
    });

    setColorOpen(false);
  };

  const handleGroup = (groupId) => {
    const value = groupId || "";

    setSelectedGroupId(value);

    applyFilter({
      groupId: value,
    });

    setGroupOpen(false);
  };

  const handleSort = (sortId) => {
    applyFilter({
      sort: sortId,
    });

    setSortOpen(false);
  };

  const toggleColor = () => {
    setColorOpen(!colorOpen);
    setGroupOpen(false);
    setSortOpen(false);
  };

  const toggleGroup = () => {
    setGroupOpen(!groupOpen);
    setColorOpen(false);
    setSortOpen(false);
  };

  const toggleSort = () => {
    setSortOpen(!sortOpen);
    setColorOpen(false);
    setGroupOpen(false);
  };

  return (
    <div className="shop-filter" ref={filterRef}>
      <div className="shop-filter__top">
        <div className="shop-filter__left">
          <div
            className={`shop-filter__select-wrap ${
              colorOpen ? "shop-filter__select-wrap--open" : ""
            }`}
          >
            <button
              type="button"
              className="shop-filter__select"
              onClick={toggleColor}
            >
              {selectedColor ? selectedColor : t("filter.color")}
              <span>&gt;</span>
            </button>

            {colorOpen ? (
              <div className="shop-filter__dropdown">
                <button type="button" onClick={() => handleColor("")}>
                  {t("filter.allColors")}
                </button>

                {colors.map((color) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => handleColor(color)}
                  >
                    {color}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div
            className={`shop-filter__select-wrap ${
              groupOpen ? "shop-filter__select-wrap--open" : ""
            }`}
          >
            <button
              type="button"
              className="shop-filter__select shop-filter__select--category"
              onClick={toggleGroup}
            >
              {selectedGroup ? getGroupName(selectedGroup) : t("filter.category")}
              <span>&gt;</span>
            </button>

            {groupOpen ? (
              <div className="shop-filter__dropdown">
                <button type="button" onClick={() => handleGroup("")}>
                  {t("filter.allCategories")}
                </button>

                {groups.map((group) => (
                  <button
                    type="button"
                    key={group.id}
                    onClick={() => handleGroup(group.id)}
                  >
                    {getGroupName(group)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="shop-filter__search">
            <input
              type="text"
              placeholder={t("filter.search")}
              defaultValue={filterParams.search || ""}
              onKeyUp={handleSearch}
            />

            <span>⌕</span>
          </div>
        </div>

        <div className="shop-filter__sort">
          <span>{t("filter.sortBy")}</span>

          <div
            className={`shop-filter__sort-wrap ${
              sortOpen ? "shop-filter__sort-wrap--open" : ""
            }`}
          >
            <button
              type="button"
              className="shop-filter__sort-button"
              onClick={toggleSort}
            >
              {selectedSort ? selectedSort.name : t("filter.sort.nameAsc")}
              <span>&gt;</span>
            </button>

            {sortOpen ? (
              <div className="shop-filter__sort-dropdown">
                {sortOptions.map((sort) => (
                  <button
                    type="button"
                    key={sort.id}
                    onClick={() => handleSort(sort.id)}
                  >
                    {sort.name}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="shop-filter__line" />
    </div>
  );
};

export default Filter;